import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { api } from '../api/client';
import { useCanEdit } from '../components/EditorOnly';
import { Stack, Typography } from '../components/mui-system';
import {
  REGISTRY_IMPORT_MAX_BYTES,
  REGISTRY_IMPORT_STATUSES,
  type RegistryImportInput,
  type RegistryImportPreview,
  type RegistryImportResult,
  type RegistryImportStatus,
} from '../../../api/src/registry/registry-import.types';
import { RESOURCE_PLATFORMS } from '../../../api/src/registry/registry-platform';
import { PlatformIcon, registryOwnershipLabels } from './RegistryCatalogTable';
import { registrySourceLink } from './RegistryResourceTable';

const statusLabels: Record<RegistryImportStatus, string> = {
  new: 'Новые',
  existing: 'Уже есть',
  duplicate: 'Повторы в списке',
  conflict: 'Конфликты',
  archived: 'В архиве',
  invalid: 'Ошибки',
};
const ownershipValues = ['owned', 'third_party', 'unknown'] as const;
const rowKinds = {
  domain: 'Весь сайт',
  website: 'Страница',
  publication: 'Страница / публикация',
  channel: 'Канал',
  account: 'Профиль',
};

export function RegistryImportDialog({
  workspaceId,
  onClose,
  onImported,
}: {
  workspaceId: string;
  onClose: () => void;
  onImported: () => void;
}) {
  const canEdit = useCanEdit('registry.import');
  const [input, setInput] = useState<RegistryImportInput>({
    text: '',
    format: 'text',
    ownership: 'owned',
  });
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<RegistryImportPreview | null>(null);
  const [result, setResult] = useState<RegistryImportResult | null>(null);
  const [busy, setBusy] = useState<'file' | 'preview' | 'save' | null>(null);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<RegistryImportStatus | ''>('');
  const active = useRef(true);
  const operation = useRef(false);
  const fileInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  function changed(patch: Partial<RegistryImportInput>) {
    setInput((current) => ({ ...current, ...patch }));
    setPreview(null);
    setResult(null);
    setError('');
    setPage(0);
    setFilter('');
  }
  async function readFile(file?: File) {
    if (!file || operation.current) return;
    operation.current = true;
    setBusy('file');
    setError('');
    setPreview(null);
    setResult(null);
    try {
      if (file.size > REGISTRY_IMPORT_MAX_BYTES)
        throw new Error('Файл должен быть не больше 256 КБ');
      const text = await file.text();
      if (active.current) {
        changed({ text, format: /\.csv$/i.test(file.name) ? 'csv' : 'text' });
        setFileName(file.name);
      }
    } catch (e) {
      if (active.current) setError(e instanceof Error ? e.message : 'Не удалось прочитать файл');
    } finally {
      operation.current = false;
      if (active.current) setBusy(null);
    }
  }
  async function check() {
    if (!canEdit || operation.current) return;
    operation.current = true;
    setBusy('preview');
    setError('');
    setPreview(null);
    setResult(null);
    try {
      const next = await api.previewRegistryImport(workspaceId, input);
      if (active.current) {
        setPreview(next);
        setPage(0);
        setFilter('');
      }
    } catch (e) {
      if (active.current) setError(e instanceof Error ? e.message : 'Не удалось проверить список');
    } finally {
      operation.current = false;
      if (active.current) setBusy(null);
    }
  }
  async function save() {
    if (!preview || !canEdit || operation.current || result || !preview.counts.new) return;
    operation.current = true;
    setBusy('save');
    setError('');
    try {
      const saved = await api.importRegistryResources(workspaceId, input, preview.fingerprint);
      if (active.current) {
        setResult(saved);
        onImported();
      }
    } catch (e) {
      if (active.current) {
        setPreview(null);
        setError(
          `${e instanceof Error ? e.message : 'Не удалось подтвердить результат импорта'}. Повторите предпросмотр перед следующей попыткой.`,
        );
      }
    } finally {
      operation.current = false;
      if (active.current) setBusy(null);
    }
  }
  const rows = preview?.rows.filter((row) => !filter || row.status === filter) ?? [];
  return (
    <Dialog open fullWidth maxWidth="lg" onClose={busy === 'save' ? undefined : onClose}>
      <DialogTitle>Импорт ресурсов</DialogTitle>
      <DialogContent>
        <Stack gap={2} pt={1}>
          {!preview && (
            <Typography color="text.secondary">
              Загрузите CSV с одной колонкой domain, url или address либо вставьте адреса строками.
              До 500 строк, 256 КБ. Ссылки без протокола дополняются https://.
            </Typography>
          )}
          <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
            <Button
              variant="outlined"
              disabled={Boolean(busy)}
              onClick={() => fileInput.current?.click()}
            >
              Выбрать CSV или TXT
            </Button>
            <input
              ref={fileInput}
              type="file"
              accept=".csv,.txt,text/csv,text/plain"
              aria-label="Файл ресурсов"
              hidden
              disabled={Boolean(busy)}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                void readFile(file);
              }}
            />
            {fileName && <Typography variant="body2">{fileName}</Typography>}
            <TextField
              select
              size="small"
              label="Формат списка"
              value={input.format}
              disabled={Boolean(busy)}
              onChange={(e) => changed({ format: e.target.value as RegistryImportInput['format'] })}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="text">Адреса строками</MenuItem>
              <MenuItem value="csv">CSV, одна колонка</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Принадлежность новых ресурсов"
              value={input.ownership}
              disabled={Boolean(busy)}
              onChange={(e) =>
                changed({ ownership: e.target.value as RegistryImportInput['ownership'] })
              }
              sx={{ minWidth: 245 }}
            >
              {ownershipValues.map((value) => (
                <MenuItem key={value} value={value}>
                  {registryOwnershipLabels[value]}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <TextField
            label="Список ресурсов"
            multiline
            minRows={preview ? 2 : 4}
            maxRows={preview ? 3 : 8}
            value={input.text}
            disabled={Boolean(busy)}
            onChange={(e) => {
              changed({ text: e.target.value });
              setFileName('');
            }}
            placeholder={'youtube.com/@brand\ntelegram.me/brand\nexample.com'}
            slotProps={{ htmlInput: { maxLength: REGISTRY_IMPORT_MAX_BYTES } }}
          />
          <Typography variant="caption" color="text.secondary">
            Существующие записи сохраняются без изменений. Отметка «Управляем», донорство и
            мониторинг при импорте не назначаются. Тип Telegram-ресурса — канал, пользователь или
            бот — по одной ссылке не определяется.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {result && (
            <Alert severity="success">
              Добавлено: {result.added}. Пропущено: {result.skipped}. Повторный импорт этого списка
              не создаст дубликаты.
            </Alert>
          )}
          {preview && (
            <>
              <Stack direction="row" gap={1} flexWrap="wrap" aria-label="Итог проверки списка">
                <Chip
                  label={`Всего: ${preview.rows.length}`}
                  onClick={() => {
                    setFilter('');
                    setPage(0);
                  }}
                  variant={filter ? 'outlined' : 'filled'}
                />
                {REGISTRY_IMPORT_STATUSES.map((status) => (
                  <Chip
                    key={status}
                    label={`${result && status === 'new' ? 'Добавлены' : statusLabels[status]}: ${preview.counts[status]}`}
                    onClick={() => {
                      setFilter(status);
                      setPage(0);
                    }}
                    variant={filter === status ? 'filled' : 'outlined'}
                    color={
                      status === 'new'
                        ? 'success'
                        : ['invalid', 'conflict'].includes(status)
                          ? 'warning'
                          : 'default'
                    }
                  />
                ))}
              </Stack>
              {!result && !preview.counts.new && (
                <Alert severity="info">
                  Новых ресурсов для добавления нет. Повторы и существующие записи будут пропущены.
                </Alert>
              )}
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table
                  size="small"
                  stickyHeader
                  sx={{ minWidth: 760 }}
                  aria-label="Предпросмотр импорта ресурсов"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Строка</TableCell>
                      <TableCell>Ресурс</TableCell>
                      <TableCell>Результат</TableCell>
                      <TableCell>Существующая запись</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.slice(page * 50, page * 50 + 50).map((row) => (
                      <TableRow key={row.line}>
                        <TableCell>{row.line}</TableCell>
                        <TableCell sx={{ maxWidth: 350, overflowWrap: 'anywhere' }}>
                          <Stack gap={0.5}>
                            <Typography variant="body2">{row.input}</Typography>
                            {row.resource && (
                              <>
                                {row.resource.address !== row.input && (
                                  <Typography variant="caption" color="text.secondary">
                                    → {row.resource.address}
                                  </Typography>
                                )}
                                <Stack direction="row" gap={1} alignItems="center">
                                  {row.platform && <PlatformIcon platform={row.platform} />}
                                  <Typography variant="caption">
                                    {row.platform && RESOURCE_PLATFORMS[row.platform].label} ·{' '}
                                    {rowKinds[row.resource.kind]} ·{' '}
                                    {registryOwnershipLabels[row.resource.ownership]}
                                  </Typography>
                                </Stack>
                              </>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 300 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {result && row.status === 'new' ? 'Добавлен' : statusLabels[row.status]}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {result && row.status === 'new' ? 'Сохранён в Реестре' : row.message}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 260, overflowWrap: 'anywhere' }}>
                          <Stack gap={0.5}>
                            {row.matches.map((match) => (
                              <Stack key={match.key}>
                                {row.status === 'archived' || busy === 'save' ? (
                                  <Typography variant="body2">{match.title}</Typography>
                                ) : (
                                  <Link
                                    component={RouterLink}
                                    to={registrySourceLink(match)}
                                    onClick={onClose}
                                  >
                                    {match.title}
                                  </Link>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                  {registryOwnershipLabels[match.ownership]}
                                </Typography>
                              </Stack>
                            ))}
                            {row.matchCount > row.matches.length && (
                              <Typography variant="caption">
                                И ещё {row.matchCount - row.matches.length}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {rows.length > 50 && (
                <TablePagination
                  component="div"
                  count={rows.length}
                  page={page}
                  rowsPerPage={50}
                  rowsPerPageOptions={[50]}
                  onPageChange={(_, next) => setPage(next)}
                />
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy === 'save'}>
          {result ? 'Готово' : 'Закрыть'}
        </Button>
        <Button
          onClick={() => void check()}
          disabled={!canEdit || Boolean(busy) || !input.text.trim()}
        >
          {busy === 'preview' ? 'Проверка…' : 'Предпросмотр'}
        </Button>
        {canEdit && (
          <Button
            variant="contained"
            onClick={() => void save()}
            disabled={Boolean(busy) || !preview?.counts.new || Boolean(result)}
          >
            {busy === 'save'
              ? 'Добавление…'
              : `Добавить новые${preview ? ` (${preview.counts.new})` : ''}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
